import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {
  fetchRulesApi,
  createRuleApi,
  editRuleApi,
  getHierarchyListApi,
} from '../api';
import {firstNonEmptyOr} from '../../../core/helpers/nonEmpty';

export interface Hierarchy {
  id: string;
  name: string;
  realmId: string | null;
  parentGroup: string | null;
}

export interface Workflow {
  id: string;
  name: string;
  status: string;
  description: string;
  version: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  startComponentId: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewComments?: string | null;
  isRejected?: boolean;
  components: Record<string, unknown>;
}

export interface WorkflowListState {
  workflows: Workflow[];
  status: string;
  page: number;
  limit: number;
  total: number;
  statusTotals: {[key: string]: number};
  loading: boolean;
  error: string | null;
  hierarchyList: Hierarchy[];
  hierarchyListLoading: boolean;
  hierarchyKey: string | null;
}

const initialState: WorkflowListState = {
  workflows: [],
  status: 'Draft', // Default to 'Draft' tab
  page: 0,
  limit: 10, // Changed default limit to 10
  total: 0,
  statusTotals: {},
  loading: false,
  error: null,
  hierarchyList: [],
  hierarchyListLoading: false,
  hierarchyKey: null,
};

export const fetchWorkflows = createAsyncThunk(
  'workflowList/fetchWorkflows',
  async ({
    status,
    page,
    limit,
    sortBy = 'updatedAt',
    sortDirection = 'DESC',
    search = '', // Add search param with default
    hierarchyKey = null, // Add hierarchyKey param
  }: {
    status: string;
    page: number;
    limit: number;
    sortBy?: string;
    sortDirection?: string;
    search?: string; // Add search param
    hierarchyKey?: string | null; // Add hierarchyKey param
  }) => {
    const response = await fetchRulesApi({
      status,
      page,
      limit,
      sortBy,
      sortDirection,
      search,
      hierarchyKey,
    });
    return response; // fetchRulesApi already returns response.data
  }
);

// Add create and edit thunks
export const createWorkflowThunk = createAsyncThunk(
  'workflowList/createWorkflow',
  async ({data}: {data: {name: string; description: string}}) => {
    const response = await createRuleApi(data);
    // createRuleApi returns response.data, so response contains { error, code, data, message }
    if (response?.data) {
      return response.data; // return the created workflow object
    }
    throw new Error(response?.message || 'Failed to create workflow');
  }
);

export const editWorkflowThunk = createAsyncThunk(
  'workflowList/editWorkflow',
  async ({
    workflowId,
    data,
  }: {
    workflowId: string;
    data: {name: string; description: string};
  }) => {
    // API expects { name, description }
    return await editRuleApi(workflowId, data);
  }
);

export const fetchHierarchyList = createAsyncThunk(
  'workflowList/fetchHierarchyList',
  async () => {
    const response = await getHierarchyListApi();
    if (response?.data && Array.isArray(response.data)) {
      return response.data as Hierarchy[];
    }
    return [];
  }
);

const workflowListSlice = createSlice({
  name: 'workflowList',
  initialState,
  reducers: {
    setWorkflowsStatus(state, action: PayloadAction<string>) {
      state.status = action.payload;
      state.page = 0;
    },
    setWorkflowsPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setWorkflowsLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
    },
    setHierarchyKey(state, action: PayloadAction<string | null>) {
      state.hierarchyKey = action.payload;
      state.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflows.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        // Map API response to state
        // The API response structure: { content: [], statusCounts: {}, totalElements: number }
        state.workflows = action.payload.content || [];
        state.total = action.payload.totalElements || 0;

        // Map statusCounts to match tab names (lowercase the keys from API)
        const apiStatusCounts = action.payload.statusCounts || {};
        state.statusTotals = {
          Draft: apiStatusCounts.draftCount || 0,
          Review: apiStatusCounts.reviewCount || 0,
          Published: apiStatusCounts.publishedCount || 0,
        };

        state.loading = false;
        state.error = null;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to fetch workflows',
          action.error.message
        );
      })
      .addCase(createWorkflowThunk.fulfilled, (state, action) => {
        state.workflows.unshift(action.payload);
        state.total += 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(createWorkflowThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to create workflow',
          action.error.message
        );
      })
      .addCase(fetchHierarchyList.pending, (state) => {
        state.hierarchyListLoading = true;
      })
      .addCase(fetchHierarchyList.fulfilled, (state, action) => {
        state.hierarchyList = action.payload;
        state.hierarchyListLoading = false;
      })
      .addCase(fetchHierarchyList.rejected, (state) => {
        state.hierarchyListLoading = false;
      });
  },
});

export const {
  setWorkflowsStatus,
  setWorkflowsPage,
  setWorkflowsLimit,
  setHierarchyKey,
} = workflowListSlice.actions;
export default workflowListSlice.reducer;
